/**
 * Plugin Browser
 * Browse and manage audio plugins (effects, instruments, analyzers)
 */

import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../../ui/button';
import {
  Plug,
  Search,
  Settings,
  Power,
  X,
  Loader2,
  ChevronRight,
  ChevronDown,
  Sliders,
  Music2,
  Activity,
  Wand2,
  Plus,
  GripVertical,
  Trash2,
  Copy,
  Save,
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Plugin type icons
const TYPE_ICONS = {
  effect: Sliders,
  instrument: Music2,
  analyzer: Activity,
  utility: Settings,
  midi: Wand2,
};

// Plugin card component
const PluginCard = ({ plugin, onInstantiate }) => {
  const TypeIcon = TYPE_ICONS[plugin.type] || Plug;
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="p-3 bg-zinc-800/50 rounded-lg border border-zinc-700 hover:border-indigo-500/50 transition-all group"
    >
      <div className="flex items-start gap-3">
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
          plugin.type === 'effect' ? 'bg-indigo-500/20' :
          plugin.type === 'instrument' ? 'bg-purple-500/20' :
          plugin.type === 'analyzer' ? 'bg-cyan-500/20' : 'bg-zinc-700'
        }`}>
          <TypeIcon className={`w-5 h-5 ${
            plugin.type === 'effect' ? 'text-indigo-400' :
            plugin.type === 'instrument' ? 'text-purple-400' :
            plugin.type === 'analyzer' ? 'text-cyan-400' : 'text-zinc-400'
          }`} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h4 className="text-sm font-medium text-white truncate">{plugin.name}</h4>
            {plugin.premium && (
              <span className="px-1.5 py-0.5 bg-amber-500/20 text-amber-400 rounded text-[10px] font-medium">
                PRO
              </span>
            )}
          </div>
          <p className="text-xs text-zinc-500 capitalize">{plugin.category}</p>
          <p className="text-xs text-zinc-600 mt-1 line-clamp-2">{plugin.description}</p>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onInstantiate(plugin)}
          className="opacity-0 group-hover:opacity-100 text-indigo-400 hover:text-indigo-300"
        >
          <Plus className="w-4 h-4" />
        </Button>
      </div>
      
      {/* Parameters preview */}
      {plugin.parameters?.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1">
          {plugin.parameters.slice(0, 4).map(param => (
            <span key={param.id} className="px-1.5 py-0.5 bg-zinc-700/50 rounded text-[10px] text-zinc-500">
              {param.name}
            </span>
          ))}
          {plugin.parameters.length > 4 && (
            <span className="px-1.5 py-0.5 text-[10px] text-zinc-600">
              +{plugin.parameters.length - 4} more
            </span>
          )}
        </div>
      )}
    </motion.div>
  );
};

// Plugin instance component
const PluginInstance = ({ instance, plugin, onUpdate, onDelete, onToggle }) => {
  const [expanded, setExpanded] = useState(false);
  const TypeIcon = TYPE_ICONS[plugin?.type] || Plug;
  
  return (
    <div className={`rounded-lg border transition-all ${
      instance.enabled
        ? 'bg-zinc-800/70 border-indigo-500/30'
        : 'bg-zinc-800/30 border-zinc-700 opacity-50'
    }`}>
      {/* Header */}
      <div className="flex items-center gap-2 p-2">
        <GripVertical className="w-4 h-4 text-zinc-600 cursor-move" />
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onToggle(instance.instance_id, !instance.enabled)}
          className={`w-6 h-6 p-0 ${instance.enabled ? 'text-green-400' : 'text-zinc-500'}`}
        >
          <Power className="w-4 h-4" />
        </Button>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-white truncate">{plugin?.name || instance.plugin_name}</p>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setExpanded(!expanded)}
          className="text-zinc-400"
        >
          {expanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onDelete(instance.instance_id)}
          className="text-zinc-400 hover:text-red-400"
        >
          <Trash2 className="w-3 h-3" />
        </Button>
      </div>
      
      {/* Parameters */}
      <AnimatePresence>
        {expanded && plugin?.parameters && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="px-3 pb-3 space-y-2 overflow-hidden"
          >
            {plugin.parameters.map(param => (
              <div key={param.id} className="space-y-1">
                <div className="flex items-center justify-between">
                  <span className="text-xs text-zinc-400">{param.name}</span>
                  <span className="text-xs text-zinc-500">
                    {instance.parameters?.[param.id]?.toFixed?.(2) ?? instance.parameters?.[param.id] ?? param.default}
                    {param.unit && ` ${param.unit}`}
                  </span>
                </div>
                {param.type === 'float' || param.type === 'int' ? (
                  <input
                    type="range"
                    min={param.min}
                    max={param.max}
                    step={param.type === 'int' ? 1 : 0.01}
                    value={instance.parameters?.[param.id] ?? param.default}
                    onChange={(e) => onUpdate(instance.instance_id, {
                      parameters: { [param.id]: parseFloat(e.target.value) }
                    })}
                    className="w-full h-1 bg-zinc-700 rounded-lg appearance-none cursor-pointer accent-indigo-500"
                    disabled={!instance.enabled}
                  />
                ) : param.type === 'bool' ? (
                  <Button
                    variant={instance.parameters?.[param.id] ? "default" : "outline"}
                    size="sm"
                    onClick={() => onUpdate(instance.instance_id, {
                      parameters: { [param.id]: !instance.parameters?.[param.id] }
                    })}
                    className="text-xs h-6"
                    disabled={!instance.enabled}
                  >
                    {instance.parameters?.[param.id] ? 'On' : 'Off'}
                  </Button>
                ) : param.type === 'select' ? (
                  <select
                    value={instance.parameters?.[param.id] ?? param.default}
                    onChange={(e) => onUpdate(instance.instance_id, {
                      parameters: { [param.id]: e.target.value }
                    })}
                    className="w-full px-2 py-1 bg-zinc-700 border border-zinc-600 rounded text-xs text-white"
                    disabled={!instance.enabled}
                  >
                    {param.options?.map(opt => (
                      <option key={opt} value={opt}>{opt}</option>
                    ))}
                  </select>
                ) : null}
              </div>
            ))}
            
            {/* Presets */}
            {plugin.presets?.length > 0 && (
              <div className="pt-2 border-t border-zinc-700">
                <p className="text-xs text-zinc-500 mb-1">Presets</p>
                <div className="flex flex-wrap gap-1">
                  {plugin.presets.map(preset => (
                    <button
                      key={preset}
                      className="px-2 py-0.5 bg-zinc-700 hover:bg-zinc-600 rounded text-xs text-zinc-300"
                    >
                      {preset}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export const PluginBrowser = ({
  isOpen,
  onClose,
  trackId = null,
  audioContext,
}) => {
  // State
  const [plugins, setPlugins] = useState([]);
  const [instances, setInstances] = useState([]);
  const [selectedType, setSelectedType] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [view, setView] = useState('browser'); // 'browser' | 'chain'

  // Load plugins on mount
  useEffect(() => {
    if (isOpen) {
      loadPlugins();
      if (trackId) {
        loadTrackChain();
      }
    }
  }, [isOpen, trackId]);

  const loadPlugins = async () => {
    setIsLoading(true);
    try {
      const url = selectedType
        ? `${API_URL}/api/plugins/list?plugin_type=${selectedType}`
        : `${API_URL}/api/plugins/list`;
      
      const response = await fetch(url);
      const data = await response.json();
      setPlugins(data.plugins || []);
    } catch (err) {
      console.error('Error loading plugins:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const loadTrackChain = async () => {
    try {
      const response = await fetch(`${API_URL}/api/plugins/track/${trackId}/chain`);
      const data = await response.json();
      setInstances(data.chain || []);
    } catch (err) {
      console.error('Error loading plugin chain:', err);
    }
  };

  // Instantiate plugin
  const instantiatePlugin = async (plugin) => {
    try {
      const response = await fetch(`${API_URL}/api/plugins/instantiate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          plugin_id: plugin.id,
          track_id: trackId,
          slot_index: instances.length,
        }),
      });
      
      if (!response.ok) throw new Error('Failed to instantiate plugin');
      
      const data = await response.json();
      setInstances(prev => [...prev, data.instance]);
      toast.success(`Added ${plugin.name}`);
    } catch (err) {
      toast.error('Failed to add plugin');
    }
  };

  // Update plugin instance
  const updateInstance = async (instanceId, updates) => {
    try {
      const response = await fetch(`${API_URL}/api/plugins/instance/${instanceId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updates),
      });
      
      if (!response.ok) throw new Error('Failed to update plugin');
      
      const data = await response.json();
      setInstances(prev => prev.map(inst =>
        inst.instance_id === instanceId ? data.instance : inst
      ));
    } catch (err) {
      toast.error('Failed to update plugin');
    }
  };

  // Delete plugin instance
  const deleteInstance = async (instanceId) => {
    try {
      await fetch(`${API_URL}/api/plugins/instance/${instanceId}`, {
        method: 'DELETE',
      });
      
      setInstances(prev => prev.filter(inst => inst.instance_id !== instanceId));
      toast.success('Plugin removed');
    } catch (err) {
      toast.error('Failed to remove plugin');
    }
  };

  // Toggle plugin enabled state
  const toggleInstance = (instanceId, enabled) => {
    updateInstance(instanceId, { enabled });
  };

  // Filter plugins
  const filteredPlugins = plugins.filter(plugin => {
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return plugin.name.toLowerCase().includes(query) ||
             plugin.category.toLowerCase().includes(query) ||
             plugin.description?.toLowerCase().includes(query);
    }
    return true;
  });

  // Get plugin details for instance
  const getPluginForInstance = (instance) => {
    return plugins.find(p => p.id === instance.plugin_id);
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          className="bg-zinc-900 rounded-2xl border border-zinc-800 w-full max-w-4xl max-h-[85vh] overflow-hidden shadow-2xl flex flex-col"
          onClick={e => e.stopPropagation()}
          data-testid="plugin-browser"
        >
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-zinc-800">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center">
                <Plug className="w-5 h-5 text-white" />
              </div>
              <div>
                <h2 className="text-lg font-bold text-white">Plugin Browser</h2>
                <p className="text-xs text-zinc-400">
                  {trackId ? `Track: ${trackId}` : 'Master Bus'}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <div className="flex bg-zinc-800 rounded-lg p-1">
                <button
                  onClick={() => setView('browser')}
                  className={`px-3 py-1 rounded text-sm ${
                    view === 'browser' ? 'bg-indigo-500 text-white' : 'text-zinc-400'
                  }`}
                >
                  Browse
                </button>
                <button
                  onClick={() => setView('chain')}
                  className={`px-3 py-1 rounded text-sm ${
                    view === 'chain' ? 'bg-indigo-500 text-white' : 'text-zinc-400'
                  }`}
                >
                  Chain ({instances.length})
                </button>
              </div>
              <Button variant="ghost" size="sm" onClick={onClose} className="text-zinc-400 hover:text-white">
                <X className="w-5 h-5" />
              </Button>
            </div>
          </div>
          
          {/* Content */}
          <div className="flex-1 overflow-hidden flex">
            {view === 'browser' ? (
              <>
                {/* Sidebar - Plugin Types */}
                <div className="w-48 border-r border-zinc-800 p-3 space-y-1">
                  <button
                    onClick={() => { setSelectedType(null); loadPlugins(); }}
                    className={`w-full px-3 py-2 rounded-lg text-left text-sm transition-all ${
                      !selectedType ? 'bg-indigo-500/20 text-indigo-400' : 'text-zinc-400 hover:bg-zinc-800'
                    }`}
                  >
                    All Plugins
                  </button>
                  {Object.entries(TYPE_ICONS).map(([type, Icon]) => {
                    const typeLabels = {
                      effect: 'Effects',
                      instrument: 'Instruments',
                      analyzer: 'Analyzers',
                      utility: 'Utilities',
                      midi: 'MIDI'
                    };
                    return (
                      <button
                        key={type}
                        onClick={() => { setSelectedType(type); loadPlugins(); }}
                        className={`w-full px-3 py-2 rounded-lg text-left text-sm flex items-center gap-2 transition-all ${
                          selectedType === type ? 'bg-indigo-500/20 text-indigo-400' : 'text-zinc-400 hover:bg-zinc-800'
                        }`}
                      >
                        <Icon className="w-4 h-4" />
                        <span>{typeLabels[type]}</span>
                      </button>
                    );
                  })}
                </div>
                
                {/* Main - Plugin List */}
                <div className="flex-1 flex flex-col">
                  {/* Search */}
                  <div className="p-3 border-b border-zinc-800">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                      <input
                        type="text"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        placeholder="Search plugins..."
                        className="w-full pl-9 pr-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-sm text-white placeholder:text-zinc-500 focus:border-indigo-500 focus:outline-none"
                      />
                    </div>
                  </div>
                  
                  {/* Plugin Grid */}
                  <div className="flex-1 overflow-y-auto p-3 space-y-2">
                    {isLoading ? (
                      <div className="flex items-center justify-center py-8">
                        <Loader2 className="w-6 h-6 text-indigo-500 animate-spin" />
                      </div>
                    ) : filteredPlugins.length === 0 ? (
                      <div className="text-center py-8">
                        <Plug className="w-10 h-10 text-zinc-600 mx-auto mb-3" />
                        <p className="text-sm text-zinc-500">No plugins found</p>
                      </div>
                    ) : (
                      filteredPlugins.map(plugin => (
                        <PluginCard
                          key={plugin.id}
                          plugin={plugin}
                          onInstantiate={instantiatePlugin}
                        />
                      ))
                    )}
                  </div>
                </div>
              </>
            ) : (
              /* Plugin Chain View */
              <div className="flex-1 overflow-y-auto p-4 space-y-2">
                {instances.length === 0 ? (
                  <div className="text-center py-8">
                    <Sliders className="w-10 h-10 text-zinc-600 mx-auto mb-3" />
                    <p className="text-sm text-zinc-500">No plugins in chain</p>
                    <p className="text-xs text-zinc-600 mt-1">Switch to Browse to add plugins</p>
                  </div>
                ) : (
                  instances.map((instance, index) => (
                    <PluginInstance
                      key={instance.instance_id}
                      instance={instance}
                      plugin={getPluginForInstance(instance)}
                      onUpdate={updateInstance}
                      onDelete={deleteInstance}
                      onToggle={toggleInstance}
                    />
                  ))
                )}
              </div>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default PluginBrowser;
